import { createContext, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client/react';

import useRouteParams from 'hooks/useRouteParams';
import { CUSTOMER_WITH_FULL_ACCOUNTS } from 'queries/customer';
import { DEBT_SALE_STATUS } from 'utilities/constants';

export const AccountsInfoContext = createContext(null);

const AccountsInfoProvider = ({ children }) => {
  const { customerId, accountId } = useRouteParams();

  const [getAccounts, accountsQuery] = useLazyQuery(
    CUSTOMER_WITH_FULL_ACCOUNTS,
  );
  const data = accountsQuery.data;

  const values = useMemo(() => {
    const simpleAccounts = data?.customer?.accounts || [];
    const simpleAccount =
      simpleAccounts.find((account) => account.id === accountId) || {};

    const accounts = data?.customer?.creditAccounts || [];
    const account =
      accounts.find((creditAccount) => creditAccount.id === accountId) || {};

    const isDebtSold = (account) =>
      account?.accountDetails?.debtSaleInfo?.status === DEBT_SALE_STATUS.SOLD;

    const accountIsArchived =
      simpleAccount?.archiveState || account?.archiveState;
    const accountIsPastDue = account?.statuses?.isPastDue;

    const accountInBankruptcy = account?.statuses?.isBankrupt;
    const accountInDebtSale = isDebtSold(account);
    const allAccountsInDebtSale =
      accounts.length > 0 && accounts.every(isDebtSold);
    const customerInDebtSale = accounts.length > 0 && accounts.some(isDebtSold);

    const contactInfo = data?.customer?.contactInfo || {};
    const statuses = account?.statuses?.raw || [];

    return {
      account,
      accountIsArchived,
      accountIsPastDue,
      accountInBankruptcy,
      accountInDebtSale,
      accounts,
      allAccountsInDebtSale,
      contactInfo,
      customerInDebtSale,
      simpleAccount,
      simpleAccounts,
      statuses,
    };
  }, [accountId, data]);

  useEffect(() => {
    if (customerId) {
      getAccounts({ variables: { customerId } });
    }
  }, [customerId]);

  return (
    <AccountsInfoContext.Provider value={{ accountsQuery, ...values }}>
      {children}
    </AccountsInfoContext.Provider>
  );
};

export default AccountsInfoProvider;
